import React, { useEffect, useReducer, useState } from 'react'
import { lockedReducer } from '../../../utils/defaultReducer'
import { Dialog, Box, DialogTitle, DialogContent, Button, Badge, IconButton, Tooltip, ListItemButton } from '@mui/material'
import UserPermissions from '../../User/Permissions'
import CloseIcon from '@mui/icons-material/Close';
import Loading from '../../Loading';
import api from '../../../api';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { getSession } from '../../../auth';
import useNotification from '../../Notification';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CopyIcon from '@mui/icons-material/ContentCopy';
import { PERIODS, TRIGGER_FIELD_OPERATORS, getTriggerFieldOptions, getConditions } from './edit';
import './styles.css'
import EditJourneyAutomation from './edit';
import ConfirmationDialogCaptcha from '../../Dialogs/ConfimationDialogCaptcha';
import { EditOptions, ContactOptions, StatusOptions } from './types';
import ReportJourneyAutomation from './report';
import { postEmailAttachments } from '../../Crm/TabButtons/SendMessage/SendEmail';
import { Message } from '@mui/icons-material';
import { getAttachments } from '../../Chat/MessagesArea/ChatWindow/MessageItem';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WidgetsIcon from '@mui/icons-material/Widgets';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const getTriggerFieldLabel = (field, opportunityCustomFields, journey) => {
    return (getTriggerFieldOptions(opportunityCustomFields, journey).find(option => option.value === field) || {}).label || field
}

const getTriggerFieldValueLabel = (field, value, opportunityCustomFields) => {
    if (field === 'status')
        return StatusOptions[value] || value

    if (String(field || '').startsWith('custom_field_')) {
        const customFieldId = String(field).replace('custom_field_', '')
        const customField = (opportunityCustomFields || []).find(item => String(item.id) === customFieldId)
        if (customField?.field_type === 'select') {
            const option = (customField.options || []).find(option => option.value === value)
            return option?.label || value
        }
    }

    return value
}

const AUTOMATION_FIELD_LABELS = {
    title: 'Nome',
    column: 'Coluna',
    trigger: 'Gatilho',
    type: 'Tipo',
    trigger_data: 'Condição',
    type_data: 'Ação',
}

const KNOWN_ERROR_MESSAGES = {
    'Column not found': 'Coluna não encontrada.',
    'Coluna não encontrada': 'Coluna não encontrada.',
    'Journey not found': 'Jornada não encontrada.',
    'Jornada não encontrada': 'Jornada não encontrada.',
}

const formatAutomationErrors = (data) => {
    if (!data) {
        return 'Algo deu errado. Tente novamente.'
    }

    if (typeof data === 'string') {
        return KNOWN_ERROR_MESSAGES[data] || data || 'Algo deu errado. Tente novamente.'
    }

    if (Array.isArray(data)) {
        const known = data.find(item => KNOWN_ERROR_MESSAGES[item])
        if (known) return KNOWN_ERROR_MESSAGES[known]
        const first = data.find(item => item && typeof item === 'string')
        return first || 'Algo deu errado. Tente novamente.'
    }

    if (typeof data !== 'object') {
        return 'Algo deu errado. Tente novamente.'
    }

    const messages = []
    Object.entries(data).forEach(([field, errors]) => {
        if (!Array.isArray(errors)) return
        const label = AUTOMATION_FIELD_LABELS[field] || field

        errors.forEach(error => {
            const errorText = String(error)

            if (KNOWN_ERROR_MESSAGES[errorText]) {
                messages.push(KNOWN_ERROR_MESSAGES[errorText])
                return
            }

            const maxLengthMatch = errorText.match(/no more than (\d+) characters?/i)
            if (maxLengthMatch) {
                messages.push(`O campo ${label} permite no máximo ${maxLengthMatch[1]} caracteres.`)
                return
            }

            const minLengthMatch = errorText.match(/at least (\d+) characters?/i)
            if (minLengthMatch) {
                messages.push(`O campo ${label} deve ter no mínimo ${minLengthMatch[1]} caracteres.`)
                return
            }

            const requiredMatch = errorText.match(/This field is required\./i)
            if (requiredMatch) {
                messages.push(`O campo ${label} é obrigatório.`)
                return
            }

            const invalidPkMatch = errorText.match(/Invalid pk .* object does not exist\./i)
            if (invalidPkMatch && field === 'column') {
                messages.push('Coluna não encontrada.')
                return
            }

            messages.push(errorText)
        })
    })

    return messages.length ? messages.join(' ') : 'Algo deu errado. Tente novamente.'
}

const initialize = async (data, setState) => {
    setState({ loader: true })
    const [rows, options] = await Promise.all([getRows(data), getOptions(data)])
    const result = { loader: false, rows: rows, ...options }
    setState(result)
}

const getRows = async (data) => {
    const enterpriseId = getSession().profile.enterprise.id
    const res = await api.get(`api/enterprises/${enterpriseId}/journeycolumn/${data.id}/automation/`)
    if (res.status === 200) {
        return res.data
    }
    return []
}

const getOptions = async (data, setState) => {
    const enterpriseId = getSession().profile.enterprise.id
    const res = await api.get(`api/enterprises/${enterpriseId}/journeycolumn/${data.id}/automation/options/`)
    if (res.status === 200) {
        return res.data
    }
    return {}
}

const handleWhatsappActivity = async (data) => {
    if (data?.mediaParameters?.headerfile && typeof (data?.mediaParameters?.headerfile) !== 'string') {
        const file = new FormData()
        file.append('file', data.mediaParameters.headerfile)
        const res = await api.post(`api/whatsapp/uploadmedia/`, file)
        if (res.status === 200) {
            data.mediaParameters.headerURL = res.data
        } else {
            useNotification(
                'Ops!',
                'Não foi possível importar a mídia.',
                'danger'
            )
            return false
        }
    }
    data.mediaParameters.headerfile = null
    return true
}

const submit = async (data, column, setState) => {
    const enterpriseId = getSession().profile.enterprise.id
    setState({ loading: true })
    if (!(await handleWhatsappActivity(data.type_data))) {
        setState({ loading: false })
        return
    }
    const emailState = data.type_data

    if (emailState.type === 'EMAIL' && emailState.emailMessageData && emailState.emailMessageData.attachments?.filter(each => !each.loaded)?.length) {
        setState({ loading: true })
        const returnData = await postEmailAttachments(emailState.emailMessageData.bodyTextContent, emailState.emailMessageData.attachments, emailState.email)
        setState({ loading: false })
        if (returnData === -1) {
            return
        }
        const attachmentregex = /\(emailattachment\)([\s\S]*?)\(\/emailattachment\)/g
        const attachments_obj = getAttachments(attachmentregex, returnData)
        emailState.emailMessageData.bodyTextContent = attachments_obj.response
        emailState.emailMessageData.attachments = [...attachments_obj.data.map(e => ({ ...e, loaded: true })), ...emailState.emailMessageData.attachments?.filter(each => each.loaded)]
    }

    const url = `api/enterprises/${enterpriseId}/journeycolumn/${column.id}/automation/`
    let res = null

    if (data.id === 0)
        res = await api.post(url, data)
    else
        res = await api.patch(url + data.id + '/', data)

    const result = { loading: false, edit: null }

    if ([200, 201].includes(res.status)) {
        useNotification(
            'Sucesso!',
            'Automação salva com sucesso!',
            'success'
        )
        const rowdata = await getRows(column)
        result.rows = rowdata
        setState(result)
    }
    else if (res.status === 400){
        useNotification(
            'Ops!',
            formatAutomationErrors(res.data),
            'danger'
        )
    }
    else {
        useNotification(
            'Ops!',
            'Houve um erro para salvar a automação!',
            'danger'
        )
    }
    setState(result)
}

const deleteAutomation = async (data, column, setState) => {
    const enterpriseId = getSession().profile.enterprise.id

    const url = `api/enterprises/${enterpriseId}/journeycolumn/${column.id}/automation/`
    let res = null
    setState({ loading: true })

    res = await api.delete(url + data.id + '/', data)

    const result = { loading: false, delete: null }

    if ([204].includes(res.status)) {
        useNotification(
            'Sucesso!',
            'Automação removida com sucesso!',
            'success'
        )
        const rowdata = await getRows(column)
        result.rows = rowdata
        setState(result)
    }
    else {
        useNotification(
            'Ops!',
            'Houve um erro para remover a automação!',
            'danger'
        )
    }
    setState(result)
}

export const AutomationItem = ({ column, setShowAutomations, disabled }) => {

    return (
        <IconButton type='button'
            disabled={disabled}
            onClick={() => { setShowAutomations(column || null) }}
        >
            <Badge
                badgeContent={column.automation_count}
                className='journey-automation-badge'
                color="primary"
                invisible={column.automation_count === 0}
            >
                <SmartToyIcon />
            </Badge>
        </IconButton>
    )

}

const NewAutomationCard = ({ onClick }) => {

    return (
        <Tooltip title='Nova Automação'>
            <ListItemButton className='journey-automation-card new' onClick={onClick}>
                <AddCircleOutlineIcon />
                Nova automação
            </ListItemButton>
        </Tooltip >
    )
}

const AutomationCard = ({ data, options, onClick, onCopy, onDelete, onReport, opportunityCustomFields, journey }) => {

    const [showButtons, setShowButtons] = useState(false)

    return (
        <ListItemButton
            onMouseEnter={() => setShowButtons(true)}
            onMouseLeave={() => setShowButtons(false)}
            className={`journey-automation-card normal ${data.active ? 'active' : 'inactive'}`} onClick={onClick}>
            <Box className={`journey-automation-card-description`}>
                <Box className='header'>{data.title || 'Título'}</Box>
                <Box className='flex'><ElectricBoltIcon /><Box className='rowitem'>Gatilho:</Box> <Box className='data'>{options[data.trigger]}</Box></Box>
                {data.trigger === "TIME_ON_COLUMN" &&
                    <Box className='flex'><AccessTimeIcon /><Box className='rowitem'>Tempo:</Box> <Box className='data'>{data.trigger_data.time} {PERIODS[data.trigger_data?.type]?.label}</Box></Box>}
                {getConditions(data.trigger_data).filter(condition => condition.field && condition.operator).map((condition, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && <Box className='flex'><Box className='rowitem'>E</Box></Box>}
                        <Box className='flex'><DisplaySettingsIcon /><Box className='rowitem'>Campo:</Box> <Box className='data'>{getTriggerFieldLabel(condition.field, opportunityCustomFields, journey)}</Box></Box>
                        <Box className='flex'><DisplaySettingsIcon /><Box className='rowitem'>Filtro:</Box> <Box className='data'>{TRIGGER_FIELD_OPERATORS[condition.operator]} {getTriggerFieldValueLabel(condition.field, condition.value, opportunityCustomFields)}</Box></Box>
                    </React.Fragment>
                ))}
                <Box className='flex'><WidgetsIcon /><Box className='rowitem'>Tipo:</Box> <Box className='data'>{options[data.type]}</Box></Box>
                {data.type === "CONTACT" && <>
                    <Box className='flex'><DisplaySettingsIcon/><Box className='rowitem'>Canal:</Box> <Box className='data'>{ContactOptions[data.type_data?.type]}</Box></Box>
                </>}
                {data.type === "EDIT" && <>
                    <Box className='flex'><TrendingUpIcon /><Box className='rowitem'>Ação:</Box> <Box className='data'>{EditOptions[data.type_data?.action]}</Box></Box>
                    {data.type_data?.action === 'STATUS' && <Box className='flex'><TrendingUpIcon /><Box className='rowitem'>Status:</Box> <Box className='data'>{StatusOptions[data.type_data?.status]}</Box></Box>}
                </>}
            </Box>
            {(showButtons) &&
                <>
                    <Tooltip title='Remover'>
                        <Box className='journey-automation-delete-card'>
                            <IconButton className='action-button delete' onClick={e => {
                                onDelete()
                                e.stopPropagation();
                            }}>
                                <DeleteIcon />
                            </IconButton>

                        </Box>
                    </Tooltip>
                    <Tooltip title='Copiar automação'>
                        <Box className='journey-automation-copy-card'>
                            <IconButton className='action-button' sx={{ color: '#21446c' }} onClick={e => {
                                onCopy()
                                e.stopPropagation();
                            }}>
                                <CopyIcon />
                            </IconButton>

                        </Box>
                    </Tooltip>
                    <Tooltip title='Relatório de execução'>
                        <Box className='journey-automation-report-card'>
                            <IconButton className='action-button' sx={{ color: '#21446c' }} onClick={e => {
                                onReport()
                                e.stopPropagation();
                            }}>
                                <AssessmentIcon />
                            </IconButton>

                        </Box>
                    </Tooltip>
                </>
            }
        </ListItemButton>
    )
}

const JourneyAutomations = ({ data, handleClose, opportunityCustomFields = null, journey = null }) => {

    const [state, setState] = useReducer(lockedReducer, {
        rows: [],
        edit: null,
        delete: null,
        report: null,
        loader: false,
        triggers: {},
        types: {}
    })

    const groups = UserPermissions()

    useEffect(() => {
        initialize(data, setState)
    }, [])

    return (
        <>

        {state.report && <ReportJourneyAutomation 
            triggers={state.triggers}
            types={state.types}
            column={data}
            data={state.report}
            handleClose={()=> setState({report: null})}
        />}
            {state.edit &&
                <EditJourneyAutomation
                    column={data}
                    data={state.edit}
                    triggers={state.triggers}
                    types={state.types}
                    opportunityCustomFields={opportunityCustomFields}
                    journey={journey}
                    handleClose={() => setState({ edit: null })}
                    submit={(payload) => { submit(payload, data, setState) }} />
            }
            {state.delete &&
                <ConfirmationDialogCaptcha
                    open={true}
                    title='Remover automação'
                    description={<>Deseja excluir a automação {state.delete.title}? Para confirmar, digite <span style={{ fontWeight: 'bold' }}>remover</span> no campo abaixo.</>}
                    captcha={'remover'}
                    handleClose={() => setState({ delete: null })}
                    handleSubmit={() => deleteAutomation(state.delete, data, setState)}
                />}
            <Dialog
                open={true}
                keepMounted
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
                maxWidth={'md'}
            >
                <Loading loading={state.loader} />
                <DialogTitle id="alert-dialog-slide-title" sx={{ display: "flex", justifyContent: "space-between" }}>
                    <span>
                        Automações de {data.title}
                    </span>
                    <Box>
                        {groups.isAdmin &&
                            <Button onClick={() => window.open("https://instant.getoutline.com/s/canal-do-cliente/doc/automacoes-nas-jornadas-S4LqVbWwL9", "_blank")}>FAQ</Button>
                        }
                        <Button sx={{ 'minWidth': '2rem', 'padding': '0' }} onClick={() => handleClose(state)} >
                            <CloseIcon />
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{display: 'flex', justifyContent: 'center'}}>
                    <Box className='column-automation-list' sx={{    gridTemplateColumns: `repeat(${state.rows.length >= 2 ?  3 : state.rows.length+1}, 1fr)`}}>
                        <NewAutomationCard onClick={() => setState({ edit: {} })} />
                        {state.rows.map(row => (
                            <AutomationCard
                                data={row}
                                options={{ ...state.triggers, ...state.types }}
                                onClick={() => setState({ edit: row })}
                                onCopy = {()=> {
                                    setState({edit: {...row, copy:true, id: 0, title: `copia_de_${row.title}`}})
                                }}
                                onDelete={() => setState({ delete: row })}
                                onReport={() => setState({ report: row })}
                                opportunityCustomFields={opportunityCustomFields}
                                journey={journey} />
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    )
}


export default JourneyAutomations